import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTable } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { BehaviorSubject, Observable, Subscription, of, timer, firstValueFrom } from 'rxjs';
import { concatMap, distinctUntilChanged, filter, flatMap, map, share, switchMap, take, tap } from 'rxjs/operators';
import { AccountsService } from '../../../accounts.service';
import { Backup, BackupService } from '../../../core/backup';
import { BackupDataSource } from '../../../core/backup/backup-data-source';
import { StagingFromBackupReply } from '../../../core/backup/backup.model';
import { SiteRouteParams } from '../../../core/routeparams';
import { SiteService, SiteStatus } from '../../../core/site';
import { StagingService } from '../../../core/staging';
import { SiteStaging } from '../../../core/staging/staging';
import { BackupDialogComponent } from '../backup-dialog/backups.component';
import { PluginInformationDialogComponent } from '../plugins/plugins.component';
import { RestoreConfirmationDialogComponent } from '../restore-dialog/restore.component';
import { MULTISITE_EDITION_NAME } from '../../../../globals';

const BACKUP_LIMIT_FOR_EXPRESS = 1;

@Component({
  selector: 'app-backup-table',
  templateUrl: './backup-table.component.html',
  styleUrls: ['./backup-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BackupTableComponent implements OnInit, OnDestroy {
  @Input() isStaging = false;

  displayedColumns = [
    'date',
    'description',
    'fileSystemSize',
    'databaseSize',
    'posts',
    'pages',
    'plugins',
    'wpVersion',
    'actions',
  ];

  dataSource: BackupDataSource;
  siteId: string;

  loading$$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  loading$: Observable<boolean> = this.loading$$.asObservable();

  backups$: Observable<Backup[]> = of([]);
  numberOfBackups$: Observable<number> = of(0);
  siteAvailable$: Observable<boolean> = of(false);
  stagingEnabled$: Observable<boolean> = of(false);
  isExpress$: Observable<boolean> = of(true);
  editionName$: Observable<string> = of('');

  progress = 0;
  interval: any;
  etaTime = '';
  backupStatus = 'done';

  subscriptions: Subscription[] = [];
  pollingInterval: any; // For polling the backup status
  countdownInterval: any; // For the countdown timer
  isCountdownRunning = false; // Flag to track if countdown is running

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatTable, { static: true }) table: MatTable<any>;
  highlightDiffs: boolean[][] = [];

  @Output() backupStatusChange = new EventEmitter<string>(); // Emit backup status to parent
  @Output() upgradeButtonClicked = new EventEmitter<void>();

  constructor(
    private route: ActivatedRoute,
    private backupService: BackupService,
    private alertService: SnackbarService,
    private siteService: SiteService,
    private router: Router,
    private dialog: MatDialog,
    private stagingService: StagingService,
    private accountsService: AccountsService,
    private translateService: TranslateService,
    private snowplow: ProductAnalyticsService,
  ) {
    this.setSiteId();
    this.updateDisplayedColumns(false);
  }

  ngOnInit(): void {
    this.loadData();
    this.dataSource = new BackupDataSource(this.backupService, this.isStaging);
    this.stagingEnabled$ = this.stagingService.currentStagingStatus$.pipe(
      map((status) => !!status && status === SiteStaging.ON),
    );
    this.backups$ = this.isStaging ? this.backupService.stagingBackups : this.backupService.backups;
    this.subscriptions.push(
      this.backups$.subscribe((backups: Backup[]) => {
        this.highlightBackupDiffs(backups);
        this.loading$$.next(false);
      }),
    );
    this.getLastBackup();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    clearInterval(this.interval);
    clearInterval(this.pollingInterval);
    clearInterval(this.countdownInterval);
  }

  setSiteId(): void {
    this.siteId = (this.route.snapshot.params as SiteRouteParams).siteId;
  }

  loadData(): void {
    this.loading$$.next(true);
    this.isExpress$ = this.accountsService.isExpress$.pipe(
      distinctUntilChanged(),
      filter((isExpress: boolean) => typeof isExpress === 'boolean'),
      tap((isExpress: boolean) => {
        this.paginator.pageSize = isExpress ? BACKUP_LIMIT_FOR_EXPRESS + 1 : 10;
        this.paginator.disabled = isExpress;
        this.getListPolling(this.paginator.pageSize, 0);
        this.table.removeFooterRowDef(null);
        this.table.renderRows();
      }),
    );
    this.editionName$ = this.accountsService.editionName$.pipe(
      distinctUntilChanged(),
      tap((edition: string) => {
        const isPremium = edition === MULTISITE_EDITION_NAME;
        this.updateDisplayedColumns(isPremium);
        this.table?.renderRows();
      }),
    );
    // Ensure the edition stream runs so we can toggle the subsites column
    this.subscriptions.push(this.editionName$.subscribe());
    this.siteAvailable$ = this.pollSiteStatus();
    this.numberOfBackups$ = this.getNumberOfBackups();
  }

  getNumberOfBackups(): Observable<number> {
    return this.backupService.numberOfBackups(this.siteId, this.isStaging);
  }

  private updateDisplayedColumns(isPremium: boolean): void {
    const baseColumns = [
      'date',
      'description',
      'fileSystemSize',
      'databaseSize',
      'posts',
      'pages',
      'plugins',
      'wpVersion',
    ];
    if (isPremium) {
      baseColumns.push('subsites');
    }
    baseColumns.push('actions');
    this.displayedColumns = baseColumns;
  }

  updateBackupStatus(status: string): void {
    this.backupStatus = status;
    this.backupStatusChange.emit(this.backupStatus); // Notify parent of status change
  }

  getLastBackup(): void {
    clearInterval(this.pollingInterval);

    this.pollingInterval = setInterval(async () => {
      const data = await firstValueFrom(this.backupService.lastBackup(this.siteId, this.isStaging).pipe(take(1)));
      if (data.length > 0) {
        const backupData: any = data[0];
        const lastBackupSize: number = data[1]?.displayFileSystemSize ?? 0;
        const etaTimeInSeconds = (lastBackupSize / 209715200) * 60; // 200MB/min

        // If backup in progress and countdown not running, start countdown
        if (
          backupData.status !== 'done' &&
          backupData.status !== 'failed' &&
          etaTimeInSeconds > 0 &&
          !this.isCountdownRunning
        ) {
          this.etaTime = this.convertETA(etaTimeInSeconds);
          this.startCountdownTimer(etaTimeInSeconds);
        }

        this.updateBackupStatus(backupData.status); // Emit updated status

        // If backup done or failed, stop everything and refresh
        if (backupData.status === 'done' || backupData.status === 'failed') {
          clearInterval(this.pollingInterval);
          clearInterval(this.countdownInterval);
          this.isCountdownRunning = false;
          this.updateBackupStatus('done');
          this.getListPolling(this.paginator.pageSize, this.paginator.pageIndex * this.paginator.pageSize);
        }
      }
    }, 2000);
  }

  startCountdownTimer(seconds: number) {
    if (this.isCountdownRunning) {
      return; // Exit if already running
    }

    this.isCountdownRunning = true;
    clearInterval(this.countdownInterval);

    const totalTime = seconds;

    this.countdownInterval = setInterval(() => {
      if (seconds > 0) {
        seconds--;
        this.etaTime = this.convertETA(seconds);
        // Progress is calculated based on elapsed time
        this.progress = Math.round(((totalTime - seconds) / totalTime) * 100);
      } else {
        // Time finished, set WAIT message and 100% progress
        clearInterval(this.countdownInterval);
        this.etaTime = this.translateService.instant('PAGES.BACKUPS.STEPS.WAIT');
        this.progress = 100;
        this.isCountdownRunning = true;
      }
    }, 1000);
  }

  getListPolling(pageSize: number, offset: number): void {
    this.subscriptions.push(this.backupService.list(this.siteId, pageSize, offset, this.isStaging).subscribe());
  }

  changedPage(pe: PageEvent): void {
    this.getListPolling(pe.pageSize, pe.pageSize * pe.pageIndex);
  }

  showRestoreSiteDialog(backup: any): void {
    if (!backup.validBackup) {
      return;
    }
    const dialogRef = this.dialog.open(RestoreConfirmationDialogComponent, {
      data: {
        backup: backup,
        isStagingBackup: this.isStaging,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((event) => !!event && event.restore),
        map((event) => (event.toStaging ? event.toStaging : false)),
        switchMap((toStaging: boolean) => {
          return toStaging
            ? this.backupService.createStagingFromBackup(this.siteId, backup.dateTime)
            : this.backupService.restore(this.siteId, backup.dateTime);
        }),
      )
      .subscribe(
        (resp: any) => {
          if (typeof resp === 'string') {
            const url = `/site/${this.siteId}/restore/${resp}`;
            this.router.navigateByUrl(url);
          } else if (resp instanceof StagingFromBackupReply) {
            this.stagingService.stagingFromBackup();
            this.navigateToStagingPage();
          }
        },
        (err) => {
          const dismissMessage = this.translateService.instant('ALERTS.GENERAL.DISMISS');
          if (err.status === 409) {
            this.alertService.openErrorSnack('Site is busy, please try later.', dismissMessage);
          } else {
            this.alertService.openErrorSnack('Error restoring backup', dismissMessage);
          }
        },
      );
  }

  showPluginDialog(backup: Backup, index: number): void {
    const backups = this.isStaging
      ? this.backupService.stagingBackups$$.getValue()
      : this.backupService.backups$$.getValue();
    this.dialog.open(PluginInformationDialogComponent, {
      data: {
        plugins: backup.listPlugins,
        timestamp: backup.dateTime,
        prevBackupPlugins: index !== backups.length - 1 ? backups[index + 1].listPlugins : [],
      },
    });
  }

  showBackupDialog(showStagingCTA?: boolean): void {
    const showBackupDialog = this.dialog.open(BackupDialogComponent, {
      data: {
        siteId: this.siteId,
        isStaging: this.isStaging,
        showStagingCTA: showStagingCTA,
      },
      disableClose: false,
      hasBackdrop: true,
    });

    this.subscriptions.push(
      showBackupDialog.componentInstance.backupCreate
        .pipe(
          flatMap(() => {
            this.siteAvailable$ = of(false);
            this.paginator.pageIndex = 0;
            return timer(0, 5000);
          }),
          concatMap(() => {
            this.getListPolling(this.paginator.pageSize, 0);
            this.getLastBackup();
            return this.pollSiteStatus();
          }),
          filter((isAvailable: boolean) => isAvailable),
          take(1),
        )
        .subscribe(() => {
          this.siteAvailable$ = of(true);
          this.numberOfBackups$ = this.getNumberOfBackups();
        }),
    );
  }

  highlightBackupDiffs(backups: Backup[]): void {
    for (let col = 0; col < 5; col++) {
      this.highlightDiffs[col] = [];
    }
    for (let row = 0; row < backups.length - 1; row++) {
      this.highlightDiffs[0][row] = backups[row].displayFileSystemSize !== backups[row + 1].displayFileSystemSize;
      this.highlightDiffs[1][row] = backups[row].displayDatabaseSize !== backups[row + 1].displayDatabaseSize;
      this.highlightDiffs[2][row] = backups[row].displayNPosts !== backups[row + 1].displayNPosts;
      this.highlightDiffs[3][row] = backups[row].displayNPages !== backups[row + 1].displayNPages;
      this.highlightDiffs[4][row] =
        backups[row].numPlugins !== backups[row + 1].numPlugins ||
        backups[row].listPlugins.some((plugin, idx) =>
          Object.keys(plugin).some((field) => plugin[field] !== backups[row + 1].listPlugins[idx][field]),
        );
    }
  }

  trackById(index: number, backup: Backup): string {
    return backup.dateTime;
  }

  isHighlighted(backupIdx: number, diffFieldIdx: number): boolean {
    return this.highlightDiffs.length > diffFieldIdx && this.highlightDiffs[diffFieldIdx][backupIdx];
  }

  getEnv(): string {
    return this.isStaging ? 'PAGES.BACKUPS.TABS.STAGING' : 'PAGES.BACKUPS.TABS.PRODUCTION';
  }

  navigateToStagingPage(): void {
    const url = `/site/${this.siteId}/staging/management`;
    this.router.navigateByUrl(url);
  }

  protected pollSiteStatus(): Observable<boolean> {
    return this.siteService.getStatus(this.siteId).pipe(
      map((status: SiteStatus) => status.available),
      distinctUntilChanged(),
      share(),
    );
  }

  convertETA(seconds: number): string {
    const units = [
      { label: 'hour', value: 3600, key: 'PAGES.BACKUPS.ETA.HOUR' },
      { label: 'minute', value: 60, key: 'PAGES.BACKUPS.ETA.MINUTE' },
      { label: 'second', value: 1, key: 'PAGES.BACKUPS.ETA.SECOND' },
    ];

    const result = units
      .map((unit) => {
        const count = Math.floor(seconds / unit.value);
        seconds %= unit.value;
        const key = this.translateService.instant(unit.key + (count > 1 ? 'S' : ''));
        return count > 0 ? `${count} ${key}` : '';
      })
      .filter(Boolean)
      .join(', ');

    return result || '0 ' + this.translateService.instant('PAGES.BACKUPS.ETA.SECONDS');
  }

  isAfterCutoffDate(dateTime: string): boolean {
    const cutoffDate = new Date('2025-10-15T08:30:00Z');
    const backupDate = new Date(dateTime);
    return backupDate >= cutoffDate;
  }
}
